/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 26th 2021, 11:03:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Fragment, lazy, Suspense } from "react";
import { useUserContext } from "authentication";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ADMIN_PORTAL_DEPLOYMENT_MODE } from "config";
import { ProtectedBaseLayout } from "core/pages";
import { AuthBaseLayout } from "authentication";
import { SERVICE_BUILDER_RELATIVE_URLS } from "service-builder";
import { CircularProgressCentered } from "ui-widgets";

const HomePage = lazy(() => import("home").then((module) => ({ default: module.HomePage })));

const LoginForm = lazy(() => import("authentication").then((module) => ({ default: module.LoginForm })));
const AdminSignUpForm = lazy(() => import("authentication").then((module) => ({ default: module.AdminSignUpForm })));
const ResetPasswordForm = lazy(() =>
  import("authentication").then((module) => ({ default: module.ResetPasswordForm }))
);
const ResetPasswordConfirmationForm = lazy(() =>
  import("authentication").then((module) => ({ default: module.ResetPasswordConfirmationForm }))
);

const VerificationEmailSent = lazy(() =>
  import("authentication").then((module) => ({ default: module.VerificationEmailSent }))
);
const AdminSSOCallback = lazy(() => import("authentication").then((module) => ({ default: module.AdminSSOCallback })));
const AdminSSOSignUpForm = lazy(() =>
  import("authentication").then((module) => ({ default: module.AdminSSOSignUpForm }))
);

const FormBuilderPage = lazy(() => import("form-builder").then((module) => ({ default: module.FormBuilderPage })));
const FormsListPage = lazy(() => import("form-builder").then((module) => ({ default: module.FormsListPage })));

const OrganizationsListingPage = lazy(() =>
  import("organizations").then((module) => ({ default: module.OrganizationsListingPage }))
);
const OrganizationFormPage = lazy(() =>
  import("organizations").then((module) => ({ default: module.OrganizationFormPage }))
);
const EditSelectedOrganizationPage = lazy(() =>
  import("organizations").then((module) => ({ default: module.EditSelectedOrganizationPage }))
);

const SetupPage = lazy(() => import("onboarding").then((module) => ({ default: module.SetupPage })));

const ServiceBuilderRoutes = lazy(() =>
  import("service-builder").then((module) => ({ default: module.ServiceBuilderRoutes }))
);

const PortalsListPage = lazy(() => import("portals").then((module) => ({ default: module.PortalsListPage })));
const PortalDetailPage = lazy(() => import("portals").then((module) => ({ default: module.PortalDetailPage })));
const PortalDetailPageWithAccessCheck = lazy(() =>
  import("portals").then((module) => ({ default: module.PortalDetailPageWithAccessCheck }))
);
const PortalContentPage = lazy(() => import("portals").then((module) => ({ default: module.PortalContentPage })));
const PortalContentForm = lazy(() => import("portals").then((module) => ({ default: module.PortalContentForm })));
const PortalContentList = lazy(() => import("portals").then((module) => ({ default: module.PortalContentList })));

const DataBankListPage = lazy(() => import("datasets").then((module) => ({ default: module.DataBankListPage })));
const DatasetPage = lazy(() => import("datasets").then((module) => ({ default: module.DatasetPage })));
const DatasetListPage = lazy(() => import("datasets").then((module) => ({ default: module.DatasetListPage })));
const DatasetImportPage = lazy(() => import("datasets").then((module) => ({ default: module.DatasetImportPage })));

const TeamsPage = lazy(() => import("teams").then((module) => ({ default: module.TeamsPage })));

const WorkerProfilePage = lazy(() => import("workers").then((module) => ({ default: module.WorkerProfilePage })));

const WorkerAcceptInvitationPage = lazy(() =>
  import("onboarding/pages/WorkerAcceptInvitationPage").then((module) => ({
    default: module.WorkerAcceptInvitationPage,
  }))
);

const SubscriptionSuccess = lazy(() =>
  import("saas-subscription").then((module) => ({ default: module.SubscriptionSuccess }))
);
const ManageBillingPage = lazy(() =>
  import("saas-subscription").then((module) => ({ default: module.ManageBillingPage }))
);

const AdminPortalProvisioningController = lazy(() =>
  import("authorization").then((module) => ({ default: module.AdminPortalProvisioningController }))
);

const OrganizationPaymentMethods = lazy(() =>
  import("payments/pages/OrganizationPaymentMethods").then((module) => ({ default: module.OrganizationPaymentMethods }))
);

const MediaLibraryPage = lazy(() =>
  import("media-library/pages/MediaLibraryPage").then((module) => ({ default: module.MediaLibraryPage }))
);

const TransaltionsPage = lazy(() =>
  import("translation").then((module) => ({
    default: module.TranslationsPage,
  }))
);

const IntegrationsPage = lazy(() =>
  import("integrations").then((module) => ({
    default: module.IntegrationsPage,
  }))
);

/*
 *
 * The main routes of the App. The core routes are wrapped around the ProtectedBaseLayout Component so they can
 * use that as the base template. Other pages e.g. setup page, can use their own templates.
 *
 */

const enum ABSOLUTE_URL_PREFIXES {
  root = "/",
  auth = "/auth/",
  onboarding = "/onboarding/",
}

enum AUTH_RELATIVE_URLS {
  login = "login/",
  logout = "logout/",
  signup = "signup/",
  resetPassword = "reset-password/",
  resetPasswordConfirmation = "reset-password-confirmation/:uid/:token/",
  verificationEmailSent = "verification-email-sent/",
  workerAcceptInvitation = "workers/invitation/accept/:invitation_token",
  adminSSOCallback = "admin-sso-callback/",
  adminSSOSignUp = "admin-sso-signup/",
}

enum ONBOARDING_RELATIVE_URLS {
  setup = "setup/",
}

// Typescript is yet to support string based enums with computed values, so we just use a javascript Object here.
enum CORE_RELATIVE_URLS {
  dashboard = "dashboard/",
  organizations = "organizations/",
  newOrganization = `organizations/new/`,
  editOrganization = `organizations/edit/:organizationId`,
  editSelectedOrganization = "edit-selected-organization/",
  databanks = "databanks/",
  datasetsByBank = "databanks/:dataBankId/datasets/",
  allDatasets = "datasets/",
  newDataset = "dataset/:dataBankId/new",
  editDataset = "dataset/:dataBankId/:datasetSchemaId",
  importData = "dataset/import/:dataBankId/:datasetSchemaId",
  portals = "portals/",
  newPortal = "portals/new",
  editPortal = "portals/edit/:portalId",
  manageContent = "portal-content/:portalId/",
  editContentPage = "portal-content/:portalId/page/:websiteMenuId",
  allTeams = "teams/",
  filteredTeams = "teams/:teamId",
  viewWorker = `workers/:workerId`,
  billing = "billing/",
  subscriptionSuccess = "subscription-success/:organizationId/",
  formBuilder = "form-builder/:formId",
  formsList = "forms/",
  organizationPaymentMethods = "organization/payment-methods/",
  mediaLibrary = "media-library/",
  translations = "translations/",
  integrations = "integrations/",
}

type APP_URL_PATH_TYPE =
  | `${ABSOLUTE_URL_PREFIXES.auth}${AUTH_RELATIVE_URLS}`
  | `${ABSOLUTE_URL_PREFIXES.onboarding}${ONBOARDING_RELATIVE_URLS}`
  | `${ABSOLUTE_URL_PREFIXES.root}${CORE_RELATIVE_URLS}`
  | `${ABSOLUTE_URL_PREFIXES.root}${SERVICE_BUILDER_RELATIVE_URLS}`;

type IABSOLUTE_URLS = {
  [k in keyof (typeof AUTH_RELATIVE_URLS &
    typeof ONBOARDING_RELATIVE_URLS &
    typeof CORE_RELATIVE_URLS &
    typeof SERVICE_BUILDER_RELATIVE_URLS)]: APP_URL_PATH_TYPE;
};

const AUTH_ABSOLUTE_URLS = Object.keys(AUTH_RELATIVE_URLS).reduce((previous, current) => {
  return { ...previous, ...{ [current]: `${ABSOLUTE_URL_PREFIXES.auth}${AUTH_RELATIVE_URLS[current]}` } };
}, {});

const ONBOARDING_ABSOLUTE_URLS = Object.keys(ONBOARDING_RELATIVE_URLS).reduce((previous, current) => {
  return { ...previous, ...{ [current]: `${ABSOLUTE_URL_PREFIXES.onboarding}${ONBOARDING_RELATIVE_URLS[current]}` } };
}, {});

const CORE_ABSOLUTE_URLS = Object.keys(CORE_RELATIVE_URLS).reduce((previous, current) => {
  return { ...previous, ...{ [current]: `${ABSOLUTE_URL_PREFIXES.root}${CORE_RELATIVE_URLS[current]}` } };
}, {});

const SERVICE_BUILDER_ABSOLUTE_URLS = Object.keys(SERVICE_BUILDER_RELATIVE_URLS).reduce((previous, current) => {
  return { ...previous, ...{ [current]: `${ABSOLUTE_URL_PREFIXES.root}${SERVICE_BUILDER_RELATIVE_URLS[current]}` } };
}, {});

export const URLS = {
  ...AUTH_ABSOLUTE_URLS,
  ...ONBOARDING_ABSOLUTE_URLS,
  ...CORE_ABSOLUTE_URLS,
  ...SERVICE_BUILDER_ABSOLUTE_URLS,
} as IABSOLUTE_URLS;

export type APP_URL_PATH_NAME_TYPE = keyof typeof URLS;

const EXCLUDED_URLS_MAPPING: Record<typeof ADMIN_PORTAL_DEPLOYMENT_MODE, string[]> = {
  ON_PREMISE: [URLS.billing],
  SAAS: [],
};

export const EXCLUDED_URLS = EXCLUDED_URLS_MAPPING[ADMIN_PORTAL_DEPLOYMENT_MODE] || [];

const ProtectedRoute = ({ element }) => {
  const { loggedInUser } = useUserContext();
  return loggedInUser ? <Fragment>{element}</Fragment> : <Navigate to={URLS.login} />;
};

const SuspenseFallback = () => {
  let SuspenseBaseLayout = ({ children }) => <>{children}</>;
  const { loggedInUser } = useUserContext();
  if (loggedInUser?.id) {
    SuspenseBaseLayout = ProtectedBaseLayout;
  }

  return (
    <SuspenseBaseLayout>
      <CircularProgressCentered />
    </SuspenseBaseLayout>
  );
};

export const OSRoutes = () => {
  return (
    <BrowserRouter basename={ABSOLUTE_URL_PREFIXES.root}>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to={URLS.dashboard} />} />

          {/* Authentication related Routes */}
          <Route path={`${ABSOLUTE_URL_PREFIXES.auth}*`} element={<AuthBaseLayout />}>
            <Route path={AUTH_RELATIVE_URLS.signup} element={<AdminSignUpForm />} />
            <Route path={AUTH_RELATIVE_URLS.verificationEmailSent} element={<VerificationEmailSent />} />
            <Route path={AUTH_RELATIVE_URLS.login} element={<LoginForm />} />
            <Route path={AUTH_RELATIVE_URLS.resetPassword} element={<ResetPasswordForm />} />
            <Route path={AUTH_RELATIVE_URLS.resetPasswordConfirmation} element={<ResetPasswordConfirmationForm />} />
            <Route path={AUTH_RELATIVE_URLS.workerAcceptInvitation} element={<WorkerAcceptInvitationPage />} />
            <Route path={AUTH_RELATIVE_URLS.adminSSOCallback} element={<AdminSSOCallback />} />
            <Route path={AUTH_RELATIVE_URLS.adminSSOSignUp} element={<AdminSSOSignUpForm />} />
          </Route>

          {/* Onboarding related Routes */}
          <Route
            path={`${ABSOLUTE_URL_PREFIXES.onboarding}*`}
            element={
              <ProtectedRoute
                element={
                  <ProtectedBaseLayout hideOrganizationSelect={true} hideDrawer={true}>
                    <Outlet />
                  </ProtectedBaseLayout>
                }
              />
            }
          >
            <Route path={ONBOARDING_RELATIVE_URLS.setup} element={<SetupPage />} />
          </Route>

          {/* Core App Routes */}
          <Route
            path={`${ABSOLUTE_URL_PREFIXES.root}*`}
            element={
              <ProtectedRoute
                element={
                  <ProtectedBaseLayout>
                    <AdminPortalProvisioningController>
                      {/* Outlet is new in react-router v6 that allows nested route components to be placed here. So we have a parent route for all routes that will use the ProtectedBaseLayout. */}
                      <Outlet />
                    </AdminPortalProvisioningController>
                  </ProtectedBaseLayout>
                }
              />
            }
          >
            <Route path={CORE_RELATIVE_URLS.dashboard} element={<HomePage />} />

            <Route path={CORE_RELATIVE_URLS.organizations} element={<OrganizationsListingPage />} />
            <Route path={CORE_RELATIVE_URLS.newOrganization} element={<OrganizationFormPage />} />
            <Route path={CORE_RELATIVE_URLS.editOrganization} element={<OrganizationFormPage />} />
            <Route path={CORE_RELATIVE_URLS.editSelectedOrganization} element={<EditSelectedOrganizationPage />} />

            <Route path={CORE_RELATIVE_URLS.portals} element={<PortalsListPage />} />
            <Route path={CORE_RELATIVE_URLS.newPortal} element={<PortalDetailPageWithAccessCheck />} />
            <Route path={CORE_RELATIVE_URLS.editPortal} element={<PortalDetailPage />} />
            <Route path={""} element={<PortalContentPage />}>
              <Route path={CORE_RELATIVE_URLS.manageContent} element={<PortalContentList />} />
              <Route path={CORE_RELATIVE_URLS.editContentPage} element={<PortalContentForm />} />
            </Route>

            <Route path={CORE_RELATIVE_URLS.databanks} element={<DataBankListPage />} />
            <Route path={CORE_RELATIVE_URLS.datasetsByBank} element={<DatasetListPage />} />
            <Route path={CORE_RELATIVE_URLS.allDatasets} element={<DatasetListPage />} />
            <Route path={CORE_RELATIVE_URLS.newDataset} element={<DatasetPage />} />
            <Route path={CORE_RELATIVE_URLS.editDataset} element={<DatasetPage />} />
            <Route path={CORE_RELATIVE_URLS.importData} element={<DatasetImportPage />} />

            <Route path={CORE_RELATIVE_URLS.allTeams} element={<TeamsPage />} />
            <Route path={CORE_RELATIVE_URLS.filteredTeams} element={<TeamsPage />} />
            <Route path={CORE_RELATIVE_URLS.viewWorker} element={<WorkerProfilePage />} />

            <Route path={CORE_RELATIVE_URLS.billing} element={<ManageBillingPage />} />
            <Route path={CORE_RELATIVE_URLS.subscriptionSuccess} element={<SubscriptionSuccess />} />
            <Route path={CORE_RELATIVE_URLS.formsList} element={<FormsListPage />} />
            <Route path={CORE_RELATIVE_URLS.formBuilder} element={<FormBuilderPage />} />
            <Route path={CORE_RELATIVE_URLS.organizationPaymentMethods} element={<OrganizationPaymentMethods />} />
            <Route path={CORE_RELATIVE_URLS.mediaLibrary} element={<MediaLibraryPage />} />
            <Route path={CORE_RELATIVE_URLS.translations} element={<TransaltionsPage />} />
            <Route path={CORE_RELATIVE_URLS.integrations} element={<IntegrationsPage />} />
            <Route path={`*`} element={<ServiceBuilderRoutes />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
